import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import React, { useRef, useEffect, useMemo } from 'react';
import classnames from 'clsx';

import { AriaHasPopup } from '../../../constants/aria-has-popup';
import { AriaWidgetRole } from '../../../constants/aria-widget-roles';
import { emptyObject } from 'ui-components/default-props/empty';
import { noop } from '../../default-props/noop';
import { Button } from '../button';
import { INLINE_MESSAGE_TYPES } from '../inline-message';
import { RequiredIndicator } from '../required-indicator';

// @ts-expect-error less import
import style from './dropdown-menu-toggler.module.less';

/**
 * @typedef {import('./dropdown-menu-toggler-types.d.ts').DropdownTogglerContentComponent} DropdownTogglerContentComponent
 * @typedef {import('./dropdown-menu-toggler-types.d.ts').DropdownMenuTogglerComponent} DropdownMenuTogglerComponent
 * @typedef {import('./dropdown-menu-toggler-types.d.ts').MemoDropdownMenuTogglerComponent} MemoDropdownMenuTogglerComponent
 * @typedef {import('./dropdown-menu-toggler-types.d.ts').AriaProps} AriaProps
 */

/**
 * @type {DropdownTogglerContentComponent}
 */
const DropdownTogglerContent = React.forwardRef(({ children, className }, ref) => (
  <div ref={ref} className={classnames(style['dropdown-toggler-content'], className)}>
    {children}
  </div>
));

DropdownTogglerContent.displayName = 'DropdownTogglerContent';

/**
 * @param {object} params
 * @param {string | undefined} params.dropdownMenuType
 * @param {boolean} params.isOpen
 * @param {string} params.dropdownMenuId
 * @returns {AriaProps}
 */
const getAriaProps = ({ dropdownMenuType, isOpen, dropdownMenuId }) => {
  const sharedDropdownProps = {
    'aria-expanded': isOpen,
    'aria-controls': dropdownMenuId,
  };

  switch (dropdownMenuType) {
    case 'ComboBox':
      return {
        ...sharedDropdownProps,
        'aria-haspopup': AriaHasPopup.ListBox,
        role: AriaWidgetRole.ComboBox,
      };
    case 'Menu':
      return {
        ...sharedDropdownProps,
        'aria-haspopup': AriaHasPopup.Menu,
      };
    default:
      return emptyObject;
  }
};

/**
 * @type {DropdownMenuTogglerComponent}
 */
export const DropdownMenuToggler = React.memo(
  React.forwardRef(
    (
      {
        buttonSize = 'normal',
        children,
        className = '',
        disabled = false,
        disabledTooltipMessage,
        hasError = false,
        inlineMessageType = '',
        hideChevron = false,
        isOpen = false,
        onBlur = noop,
        onClick = noop,
        onFocus = noop,
        onKeyDown = noop,
        openClassName = '',
        amplitudeTrackingId,
        required = false,
        shouldSendFocusToInput = false,
        tabIndex,
        testId = '',
        theme = '',
        variant = 'button',
        chevronClassname = '',
        buttonOtherProperties = emptyObject,
        id,
        dropdownMenuId,
        dropdownMenuType,
        ariaLabel,
        dropdownTogglerContentClassName,
      },
      ref,
    ) => {
      const buttonClasses = classnames(
        className,
        { [openClassName]: isOpen },
        style['dropdown-menu-toggler'],
        {
          [style.simple]: variant === 'form',
          [style.childless]: !children,
          [style['has-error']]: hasError || inlineMessageType === INLINE_MESSAGE_TYPES.error,
        },
        style[theme],
      );

      const buttonRef = useRef(null);
      const actualButtonRef = ref || buttonRef;

      // TODO: This should be done differently. We shouldn't be touching component instances
      // directly, but rather, in this situation, passing this information via props
      useEffect(() => {
        if (shouldSendFocusToInput && actualButtonRef && 'current' in actualButtonRef && actualButtonRef.current) {
          actualButtonRef.current.focus();
        }
      }, [actualButtonRef, shouldSendFocusToInput]);

      // Only add id to button if given a dropdownMenuType so that the appropriate aria attributes are added
      const buttonId = dropdownMenuType ? id : undefined;

      const otherProperties = useMemo(() => {
        const ariaProps = getAriaProps({
          dropdownMenuType,
          isOpen,
          dropdownMenuId,
        });

        return {
          ...ariaProps,
          ...buttonOtherProperties,
        };
      }, [buttonOtherProperties, dropdownMenuId, dropdownMenuType, isOpen]);

      return (
        <Button
          ref={actualButtonRef}
          ariaLabel={ariaLabel}
          buttonSize={buttonSize}
          className={buttonClasses}
          disabled={disabled}
          disabledTooltipMessage={disabledTooltipMessage}
          id={buttonId}
          onBlur={onBlur}
          onClick={onClick}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          otherProperties={otherProperties}
          amplitudeTrackingId={amplitudeTrackingId}
          tabIndex={tabIndex}
          testId={testId}
          theme={theme}
        >
          <DropdownTogglerContent className={dropdownTogglerContentClassName}>{children}</DropdownTogglerContent>
          {required ? <RequiredIndicator className={classnames(style['required-indicator'], style[theme])} /> : null}
          {!hideChevron && (
            <div className={classnames(style['dropdown-toggler-chevron'], { [style.open]: isOpen }, chevronClassname)}>
              <FontAwesomeIcon className={style.chevron} icon={faCaretDown} />
            </div>
          )}
        </Button>
      );
    },
  ),
);
